import { useCallback, useEffect, useMemo, useState } from "react";
import bonificacionesService from "../../../../services/bonificacionesService";
import { normalizeBonificacion } from "../adapters/bonificacionesAdapter";
import { initialBonificacionFilters } from "../constants";
import { showToast } from "../../../../utils/showToast";

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseDateFilter = (value, { endOfDay = false } = {}) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(
      year,
      month - 1,
      day,
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 999 : 0
    );
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export default function useBonificacionesList() {
  const [bonificaciones, setBonificaciones] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialBonificacionFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchBonificaciones = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bonificacionesService.listar();
      const parsed = (Array.isArray(data) ? data : []).map(normalizeBonificacion);
      setBonificaciones(parsed);
      // Refrescar seleccionado desde los datos nuevos para que el panel refleje cambios
      setSeleccionado((prev) => (prev ? (parsed.find((b) => b.id === prev.id) ?? null) : null));
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo obtener las bonificaciones.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBonificaciones();
  }, [fetchBonificaciones]);

  const resumen = useMemo(() => {
    const total = bonificaciones.length;
    const estados = bonificaciones.reduce(
      (acc, bonificacion) => {
        const estado = bonificacion.estado || "sin_estado";
        acc[estado] = (acc[estado] || 0) + 1;
        acc.monto += bonificacion.monto || 0;
        return acc;
      },
      { monto: 0 }
    );

    return {
      total,
      pendientes: estados.pendiente || 0,
      no_pagadas: estados.no_pagada || 0,
      pagadas: estados.pagada || 0,
      anuladas: estados.anulada || 0,
      montoTotal: estados.monto || 0,
    };
  }, [bonificaciones]);

  const bonificacionesFiltradas = useMemo(() => {
    return bonificaciones.filter((bonificacion) => {
      if (filtros.estado !== "all" && filtros.estado && bonificacion.estado !== filtros.estado) {
        return false;
      }

      const fechaGeneracion = parseDate(bonificacion.fechaGeneracion || bonificacion.createdAt);
      const desde = parseDateFilter(filtros.fecha_desde);
      const hasta = parseDateFilter(filtros.fecha_hasta, { endOfDay: true });
      if (desde && fechaGeneracion && fechaGeneracion < desde) return false;
      if (hasta && fechaGeneracion && fechaGeneracion > hasta) return false;

      if (filtros.search) {
        const term = filtros.search.toLowerCase();
        const matches =
          `${bonificacion.id}`.includes(term) ||
          (bonificacion.referente || "").toLowerCase().includes(term) ||
          (bonificacion.referido || "").toLowerCase().includes(term);
        if (!matches) return false;
      }

      return true;
    });
  }, [bonificaciones, filtros]);

  const total = bonificacionesFiltradas.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filtros, total]);

  const bonificacionesPaginadas = useMemo(() => {
    const start = (page - 1) * pageSize;
    return bonificacionesFiltradas.slice(start, start + pageSize);
  }, [bonificacionesFiltradas, page, pageSize]);

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const seleccionar = useCallback((bonificacion) => {
    setSeleccionado(bonificacion || null);
  }, []);

  const limpiarFiltros = useCallback(() => setFiltros(initialBonificacionFilters), []);

  const actualizarBonificacion = useCallback((id, updates) => {
    if (!id) return;
    setBonificaciones((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } : item)));
    setSeleccionado((prev) => (prev && prev.id === id ? { ...prev, ...updates } : prev));
  }, []);

  return {
    bonificaciones,
    bonificacionesFiltradas,
    seleccionado,
    setSeleccionado,
    loading,
    error,
    resumen,
    filtros,
    setFiltros,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    bonificacionesPaginadas,
    reload: fetchBonificaciones,
    seleccionar,
    actualizarBonificacion,
  };
}
